import { useState, useEffect } from "react";
import Logs from "@/components/common/log/logs";
import { useGetLogs, useDownloadLogs } from "@/queries/process";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import Filter from "@/components/ui/filters";
import type {
  SortOptionsTypes,
  SortDirectionType,
  FilterTypeList,
  CurrentFiltersType,
  MultiEntitySelections,
} from "@/types/common/filters";
import {
  sortDirectionEnum,
  filterTypesEnum,
  searchTypesEnum,
} from "@/types/common/filters";
import { mergeFilters } from "@/utils/filters";
import { useGetUsersAsInfinite } from "@/queries/users/users.api";
import type { LogItemType } from "@/types/common/logs";
import { Skeleton } from "@/components/ui/skeleton";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";

interface RoleHistoryProps {
  roleId: string | undefined;
}

const sortFields: SortOptionsTypes[] = [
  { label: "Fecha", value: "created_at" },
  { label: "Usuario", value: "causer.name" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha de creación",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
  {
    label: "Creador del cambio",
    type: filterTypesEnum.textsearch,
    field: "causer_id",
    searchField: "search",
    query: useGetUsersAsInfinite,
    searchType: searchTypesEnum.user,
  },
];

export const RoleHistory = ({ roleId }: RoleHistoryProps) => {
  // State for table filters
  const [filters, setFilters] = useState<CurrentFiltersType[]>([]);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});

  // Sorting state
  const [orderField, setOrderField] = useState<string>("created_at");
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.desc,
  );

  // Build filter object for API
  const queryFilters = {
    ...(filters.length > 0 ? mergeFilters(filters) : {}),
    "properties->keycloak_role_id": roleId,
  };

  const {
    data: logsResponse,
    error,
    isLoading,
  } = useGetLogs({
    filters: queryFilters,
    params: {
      order_by: orderField
        ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
        : "!created_at",
      relationships: "causer",
    },
  });

  const { mutateAsync: downloadLogs, isPending } = useDownloadLogs({
    filters: queryFilters,
    params: {
      order_by: orderField
        ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
        : "!created_at",
      relationships: "causer",
    },
  });

  const [recentLogs, setRecentLogs] = useState<LogItemType[]>([]);
  const [yesterdayLogs, setYesterdayLogs] = useState<LogItemType[]>([]);
  const [pastLogs, setPastLogs] = useState<LogItemType[]>([]);

  useEffect(() => {
    if (!logsResponse?.data) return;

    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(todayStart.getDate() - 1);

    const formatted: LogItemType[] = logsResponse.data.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (log: any, idx: number) => ({
        id: Number(log.id ?? idx),
        description: log.description || "-",
        action: log.event || log.description || "-",
        author: log.causer
          ? `${log.causer.name} ${log.causer.last_name || ""}`.trim()
          : "Desconocido",
        date: log?.created_at ? new Date(log.created_at) : new Date(),
        subject_type: log?.subject_type,
        subject: log?.subject ?? null,
        properties: log?.properties ?? {},
      }),
    );

    const recent: LogItemType[] = [];
    const yesterday: LogItemType[] = [];
    const past: LogItemType[] = [];

    formatted.forEach((item) => {
      const date = item.date;
      if (date >= todayStart) {
        recent.push(item);
      } else if (date >= yesterdayStart && date < todayStart) {
        yesterday.push(item);
      } else {
        past.push(item);
      }
    });

    setRecentLogs(recent);
    setYesterdayLogs(yesterday);
    setPastLogs(past);
  }, [logsResponse]);

  const handleApplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    if (field) {
      setOrderField(field);
      setOrderDirection(direction);
    }
  };

  const renderBlocks = () => {
    const blocks = [
      { title: "Recientemente", items: recentLogs },
      { title: "Ayer", items: yesterdayLogs },
      { title: "Pasados", items: pastLogs },
    ];

    const sortedBlocks =
      orderDirection === sortDirectionEnum.asc ? blocks.reverse() : blocks;

    return sortedBlocks.map(
      (block) =>
        block.items.length > 0 && (
          <Logs key={block.title} title={block.title} items={block.items} />
        ),
    );
  };

  const handleDownloadLogs = async () => {
    try {
      await downloadLogs();
    } catch (error) {
      console.error("Error downloading logs:", error);
    }
  };

  return (
    <>
      <div className="flex justify-between py-6" data-testid="role-history">
        <div className="flex space-x-3">
          <SortDropdown
            currentOrderField={orderField}
            currentOrderDirection={orderDirection}
            sortOptions={sortFields}
            onClick={handleApplySort}
          />
          <Filter
            filters={FilterFields}
            currentFilters={filters}
            onCurrentFiltersChange={setFilters}
            selecteds={selectedEntities}
            onSetSelecteds={setSelectedEntities}
          />
        </div>
        <Button
          variant="quiet"
          onClick={handleDownloadLogs}
          className={cn(
            "leading-2xl shadow-custom py-1.5 px-2 rounded-lg min-w-9 gap-2 font-semibold text-body text-sm font-geist cursor-pointer relative",
            isPending
              ? "border-neutral-silent/20 text-on-disabled"
              : "border-neutral shadow-card text-body",
          )}
          disabled={isPending}
        >
          Exportar CSV
          <Icon type="far" name="file-spreadsheet" />
        </Button>
      </div>

      <div className="bg-neutral-object p-4 rounded-lg border border-neutral-quiet [@media(max-height:800px)]:max-h-full h-[calc(100vh-265px)] overflow-y-auto">
        {isLoading ? (
          <div role="status" className="max-w-sm animate-pulse">
            <Skeleton className="h-3 rounded-full w-48 mb-4" />
            <Skeleton className="h-2 rounded-full max-w-[380px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[340px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[320px] mb-2.5" />
          </div>
        ) : error ? (
          <p className="text-danger">Error al cargar registros</p>
        ) : (
          <div className="space-y-10">{renderBlocks()}</div>
        )}
      </div>
    </>
  );
};
